import { z } from "zod";

import {
  ORGANIZATION_INVITATION_STATUS,
  ORGANIZATION_TYPE,
  TEAM_MEMBER_ROLE,
} from "~/admins/constants/organizations";
import { TEAM_MEMBER_PERMISSIONS, TEXT_SHORT_LENGTH } from "~/common/constants";
import { permissionSchema, teamMemberSchema } from "~/common/utils/schemas";

export const organizationSettingSchema = z.object({
  created_at: z.string(),
  ai_informant_feature: z.boolean(),
  zoom_feature: z.boolean(),
  spruce_feature: z.boolean(),
  updated_at: z.string(),
});

export const organizationSettingUpdateSchema = z.object({
  ai_informant_feature: z.boolean(),
  zoom_feature: z.boolean(),
  spruce_feature: z.boolean(),
  organization_id: z.string(),
});

const organizationBaseResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(ORGANIZATION_TYPE),
  created_at: z.string(),
  settings: z.array(organizationSettingSchema),
});

export const organizationAddressSchema = z.object({
  street: z.string().nullable(),
  apartment: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipcode: z.string().nullable(),
});

export const organizationContactSchema = z.object({
  id: z.string().optional(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  phone_number: z.string(),
});

export const organizationDetailResponseSchema = organizationBaseResponseSchema
  .omit({ created_at: true })
  .extend({
    internal_note: z.string().nullable(),
    address: organizationAddressSchema,
    contacts: z.array(organizationContactSchema),
    permissions: z.array(permissionSchema),
  });

export const organizationInvitationStatusSchema = z.nativeEnum(
  ORGANIZATION_INVITATION_STATUS,
);

export const organizationSeatDetailResponseSchema = z.object({
  expires_at: z.string(),
  created_at: z.string(),
  last_status_change_at: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  id: z.uuid(),
  invited_by: z.uuid(),
  organization_id: z.uuid(),
  permissions: z.array(permissionSchema),
  role: z.enum(TEAM_MEMBER_ROLE),
  status: organizationInvitationStatusSchema,
  team_member_email: z.email(),
});

export const organizationSeatListResponseSchema = z.object({
  created_at: z.string(),
  deactivated_at: z.string().nullable(),
  id: z.uuid(),
  organization_id: z.uuid(),
  team_member_id: z.uuid(),
  team_member: teamMemberSchema,
});

export const organizationListResponseSchema =
  organizationBaseResponseSchema.extend({
    seats_count: z.number(),
  });

export const updateOrganizationRequestSchema = organizationBaseResponseSchema
  .pick({ name: true, id: true })
  .extend({
    name: z.string(),
    address: organizationAddressSchema,
    contacts: z.array(
      organizationContactSchema.extend({
        id: z.string().optional(),
        email: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        phone_number: z.string(),
      }),
    ),
  });

export const updateOrganizationFormSchema = z.object({
  name: z
    .string({
      error: "Name is required",
    })
    .min(1, { error: "Name is required" })
    .max(TEXT_SHORT_LENGTH, "Name must be less than 200 characters"),
  street: z
    .string()
    .max(TEXT_SHORT_LENGTH, "Street must be less than 200 characters")
    .nullable(),
  apartment: z
    .string()
    .max(TEXT_SHORT_LENGTH, "Apartment must be less than 200 characters")
    .nullable(),
  city: z
    .string()
    .max(TEXT_SHORT_LENGTH, "City must be less than 200 characters")
    .nullable(),
  state: z.string().nullable(),
  zipcode: z
    .string()
    .nullable()
    .refine((code) => !code || code.length >= 5, {
      message: "Zipcode must contain at least 5 characters",
    }),
  primaryContactEmail: z.string().min(1, "Email is required").email(),
  primaryContactFirstName: z
    .string({ error: "Contact name is required" })
    .min(1, { error: "Contact name is required" })
    .max(TEXT_SHORT_LENGTH, "Contact name must be less than 200 characters"),
  primaryContactLastName: z
    .string({ error: "Contact last name is required" })
    .min(1, { error: "Contact last name is required" })
    .max(
      TEXT_SHORT_LENGTH,
      "Contact last name must be less than 200 characters",
    ),
  primaryContactPhoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, {
      error: "Must be a valid phone number",
    }),
});

export const addOrganizationRequestSchema = organizationBaseResponseSchema.pick(
  {
    name: true,
    type: true,
  },
);

export const addOrganizationFormSchema = addOrganizationRequestSchema.extend({
  name: z
    .string()
    .min(1, "Name is required")
    .max(TEXT_SHORT_LENGTH, "Name must be less than 200 characters"),
  type: z.enum(ORGANIZATION_TYPE, { error: "Type is required" }),
});

export const addOrganizationSeatRequestSchema = z.object({
  email: z.email(),
  role: z.enum(TEAM_MEMBER_ROLE),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  organization_id: z.uuid(),
  permissions: z.array(z.enum(TEAM_MEMBER_PERMISSIONS)),
});

export const addOrganizationSeatFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(TEXT_SHORT_LENGTH, "First name must be less than 200 characters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(TEXT_SHORT_LENGTH, "Last name must be less than 200 characters"),
  email: z.email("Must be a valid email"),
  role: z.enum(TEAM_MEMBER_ROLE, { error: "Role is required" }),
  permissions: z.array(z.enum(TEAM_MEMBER_PERMISSIONS)),
});

export const updateOrganizationPrimaryProviderFormSchema = z.object({
  primaryProvider: z.string({ error: "Provider is required" }).nullable(),
});

export const teamMemberPermissionsUpdateSchema = z.object({
  team_member_id: z.uuid(),
  permissions: z.array(z.enum(TEAM_MEMBER_PERMISSIONS)),
});
